/**
 * The FF house style: the fonts, colors and corner radii every pasted section
 * should use. It is versioned and editable in Settings; every AI brief carries
 * the current version, and every page gets it as --gx-* CSS variables.
 *
 * --gx- is used because live widgets already define their own --ff-* names
 * with different values; nothing uses --gx- yet, so adding these changes no
 * existing page.
 */

export type HouseColor = { token: string; value: string; use: string };

export type HouseStyle = {
  version: number;
  fontEn: string;
  fontZh: string;
  colors: HouseColor[];
  radiusButton: string;
  radiusCard: string;
  notes?: string;
  createdBy?: string | null;
  createdAt?: string | null;
};

/** v1: drafted from the most-used values in live ff.com widget code (option A). */
export const HOUSE_STYLE_V1: HouseStyle = {
  version: 1,
  fontEn: '\'Roboto\', sans-serif',
  fontZh: '\'Noto Sans SC\', \'Source Han Sans SC\', \'PingFang SC\', sans-serif',
  colors: [
    { token: 'ink', value: '#1d1d1f', use: 'main text' },
    { token: 'paper', value: '#f5f5f7', use: 'light section backgrounds' },
    { token: 'white', value: '#ffffff', use: 'white backgrounds and text on dark' },
    { token: 'night', value: '#111111', use: 'dark sections' },
    { token: 'graphite', value: '#48484c', use: 'secondary text' },
    { token: 'gray', value: '#86868b', use: 'muted text' },
    { token: 'silver', value: '#d8d8dc', use: 'lines and borders' },
    { token: 'bronze', value: '#ad7c59', use: 'accent, used sparingly' }
  ],
  radiusButton: '980px',
  radiusCard: '20px',
  notes: 'Drafted from the live site (option A).'
};

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const TOKEN = /^[a-z][a-z0-9-]{0,30}$/;
const LENGTH = /^(0|\d+(\.\d+)?(px|rem|em|%))$/;
// Font stacks: names, quotes, commas, spaces and hyphens only, so nothing can break out of the CSS.
// A comma list of families, each a bare name (Noto Sans SC) or a quoted one ('PingFang SC'):
// quotes must pair up, or the rest of the :root rule would be swallowed by the open string.
const FAMILY = ' *(?:\'[\\w -]+\'|"[\\w -]+"|[\\w-]+(?: +[\\w-]+)*) *';
const FONT = new RegExp(`^${FAMILY}(?:,${FAMILY})*$`);
const fontOk = (stack?: string) => !!stack && stack.length <= 200 && FONT.test(stack);
export const MAX_COLORS = 40;

/** Problems that would make the style unusable or unsafe to put in a page; empty when it's fine. */
export const validateHouseStyle = (style: Omit<HouseStyle, 'version'>): string[] => {
  const problems: string[] = [];
  if (!fontOk(style.fontEn)) problems.push('The English font stack must be font names separated by commas, with any quotes in pairs.');
  if (!fontOk(style.fontZh)) problems.push('The Chinese font stack must be font names separated by commas, with any quotes in pairs.');
  if (!LENGTH.test(style.radiusButton || '')) problems.push('The button radius must be a length like 980px.');
  if (!LENGTH.test(style.radiusCard || '')) problems.push('The card radius must be a length like 20px.');
  const colors = style.colors || [];
  if (!colors.length) problems.push('Add at least one color.');
  // Every color is written into every page's head; a palette doesn't need more.
  if (colors.length > MAX_COLORS) problems.push(`Use at most ${MAX_COLORS} colors.`);
  const seen = new Set<string>();
  colors.forEach((c) => {
    if (!TOKEN.test(c.token || '')) problems.push(`"${c.token}" isn’t a valid color name (lowercase letters, numbers and hyphens).`);
    else if (seen.has(c.token)) problems.push(`The color name "${c.token}" is used twice.`);
    seen.add(c.token);
    if (!HEX.test(c.value || '')) problems.push(`${c.token || 'A color'} needs a hex value like #1d1d1f.`);
  });
  return problems;
};

// What a color is for: one short line of plain words. It goes into AI briefs and the AI guide as text an
// assistant reads, so no line breaks or characters that could start code blocks, headings or tags there.
const USE_TEXT = /^[^\u0000-\u001f\u007f-\u009f\u2028\u2029`<>{}|#*\\]{0,120}$/; // eslint-disable-line no-control-regex

/**
 * Problems with the "what it's for" texts, checked when someone saves. Not part of validateHouseStyle:
 * these texts never reach a page, so an old saved style shouldn't stop working on pages because of one.
 */
export const validateColorUses = (colors: HouseColor[]): string[] => (colors || [])
  .filter(c => !USE_TEXT.test(c.use || ''))
  .map(c => `What “${c.token}” is for must be one short line of plain words (no line breaks, backticks or # < > { } | * \\).`);

/** A color's "what it's for" text, safe to put in a brief whatever was saved before validateColorUses. */
export const cleanColorUse = (use: string): string => String(use || '')
  .replace(/[\u0000-\u001f\u007f-\u009f\u2028\u2029]/g, ' ') // eslint-disable-line no-control-regex
  .replace(/[`<>{}|#*\\]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, 120);

/** The --gx-* variables for a page's head. Assumes a valid style. */
export const houseStyleCss = (style: HouseStyle): string => {
  const vars = [
    `--gx-font:${style.fontEn}`,
    `--gx-font-zh:${style.fontZh}`,
    ...style.colors.map(c => `--gx-${c.token}:${c.value.toLowerCase()}`),
    `--gx-radius-button:${style.radiusButton}`,
    `--gx-radius-card:${style.radiusCard}`
  ];
  return `:root{${vars.join(';')}}`;
};
